/**
 * Predefined approve / reject reasons for POD decision popups.
 *
 * The string values here are canonical (English) and are what gets persisted
 * into the approval/rejection remark, so they double as both the option value
 * and the stored audit text. `Others` is the escape hatch that requires the
 * approver to type a free-text remark instead.
 */

export const OTHERS_REASON = 'Others' as const

export const APPROVE_REASONS = ['Without Stamp Approved', OTHERS_REASON] as const

export const REJECT_REASONS = [
  'No stamp',
  'Third party stamp',
  'Excess Quantity',
  'Short quantity',
  'Stamp not visible',
  'Wrong POD',
  OTHERS_REASON,
] as const

export type DecisionStatus = 'Approved' | 'Rejected'

export function reasonsFor(status: DecisionStatus): readonly string[] {
  return status === 'Approved' ? APPROVE_REASONS : REJECT_REASONS
}

/**
 * Compose the remark that is sent to the backend (and stored in
 * approval_remark / rejection_remark).
 *
 * - `Others`         -> just the free-text note (required upstream, so present)
 * - predefined reason -> the reason alone, or "reason — note" when the approver
 *                        also typed an optional note.
 */
export function buildRemarks(reason: string, extra?: string | null): string {
  const note = (extra ?? '').trim()
  if (reason === OTHERS_REASON) return note
  return note ? `${reason} — ${note}` : reason
}
